<template>
  <div
    class="rs-form-checkbox-search"
    :class="{ 'rs-form-checkbox-search--dividers': dividers }"
  >
    <div
      v-if="isSearchable"
      class="mt-3 d-flex flex-row justify-content-between"
    >
      <RsFormInput
        v-if="!hideSearchInput"
        v-model="searchText"
        icon="search"
        :debounce="300"
        type="search"
        :placeholder="placeholder"
      />
      <div class="d-flex">
        <RsButton
          variant="secondary"
          hollow
          @click="handleSelectAll(true)"
        >
          {{ $ts('actions.selectAll') }}
        </RsButton>
        <RsButton
          variant="secondary"
          class="rs-modal-btn-secondary-margin"
          hollow
          @click="handleSelectAll(false)"
        >
          {{ $ts('actions.deselectAll') }}
        </RsButton>
      </div>
    </div>
    <div>
      <OverlayScrollbars
        ref="scrollbar"
        class="rs-scroll"
        :style="{ height: `160px` }"
      >
        <RsCheckboxGroup
          :id="rsCheckboxGroupId"
          :columns="columns"
          :selected="boundValue"
          :options="searchResults"
          @change="$emit('input', $event); $emit('update:modelValue', $event)"
        />
      </OverlayScrollbars>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RsFormInput from '@/components/_global/RsFormInput.vue';
import { ISelectOption } from '@/shared';

interface ICheckboxSearchState {
  searchText: string,
}

export default defineComponent({
  name: 'RsFormCheckboxSearch',

  components: { RsFormInput },

  props: {
    options: {
      type: Array as () => ISelectOption[],
      default: () => [],
    },
    value: {
      type: Array as () => string[] | number[],
      default: () => [],
    },
    /**
     * Vue 3 v-model binding, kept alongside the legacy `value`/`input` pair so
     * both `v-model` and explicit `:value`+`@input` consumers work. `undefined`
     * means the parent did not bind v-model (see the `boundValue` computed).
     */
    modelValue: {
      type: Array as () => string[] | number[],
      default: undefined,
    },
    columns: {
      type: Number,
      default: 2,
    },
    rsCheckboxGroupId: {
      type: String,
      default: '',
    },
    isSearchable: {
      type: Boolean,
      default: true,
    },
    /**
     * Hides just the search input while `isSearchable` keeps the Select All/Deselect All
     * buttons visible.
    */
    hideSearchInput: {
      type: Boolean,
      default: false,
    },
    /** Renders a divider line beneath each checklist option. */
    dividers: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
  },

  emits: ['input', 'update:modelValue', 'change'],

  data(): ICheckboxSearchState {
    return {
      searchText: '',
    };
  },

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): string[] | number[] {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    searchResults(): ISelectOption[] {
      return this.options?.filter((option) => option.text.toLowerCase().includes(this.searchText.toLowerCase())) || [];
    },
  },

  methods: {
    handleSelectAll(isAll: boolean) {
      const selectedOptions = (this.options.length && isAll) ? this.options.map((city: ISelectOption) => city.value) : [];
      this.$emit('input', selectedOptions);
      this.$emit('update:modelValue', selectedOptions);
    },
  },
});

</script>

<style lang="scss">
@import '@/styles/_variables.scss';

.rs-form-checkbox-search {
  .rs-checkbox {
    margin-bottom: 3px !important;
  }

  &--dividers {
    .rs-checkbox-group > li {
      padding-bottom: 12px;
      margin-bottom: 12px;
      border-bottom: 1px solid $tertiary-gray;

      &:last-child {
        margin-bottom: 0;
        padding-bottom: 0;
        border-bottom: none;
      }
    }
  }
}

</style>
